import type { TroopKind } from "../../../data/portal-ffa-catalog";
import { FfaEvent } from "../sim-events";
import {
  hitProfileFor,
  resolveAttackHits,
  type HitProfile,
} from "./combat";
import type { SimEntity, UnitRole, WorldState } from "./types";
import { C, dist, emit, unitDef } from "./shared";
import { applyFacingFromIntent, setDesiredToward } from "./motion-steering";

export type CombatSteerResult = {
  killed: boolean;
};

function combatParams(): {
  threatMul: number;
  acquireMul: number;
  projectileRoles: string[];
  marchInterruptMul: number;
  marchInterruptStandoffMul: number;
  marchInterruptCapPx: number;
  leashSlackPx: number;
  engageAcquirePadPx: number;
  engageLeashAcquireMul: number;
  leashPullbackGoalBias: number;
} {
  const c = (
    C as {
      combat?: {
        threatMul?: number;
        acquireMul?: number;
        projectileRoles?: string[];
        marchInterruptMul?: number;
        marchInterruptStandoffMul?: number;
        marchInterruptCapPx?: number;
        leashSlackPx?: number;
        engageAcquirePadPx?: number;
        engageLeashAcquireMul?: number;
        leashPullbackGoalBias?: number;
      };
    }
  ).combat;
  return {
    threatMul: Number(c?.threatMul) || 1.35,
    acquireMul: Number(c?.acquireMul) || 1,
    projectileRoles: Array.isArray(c?.projectileRoles)
      ? c.projectileRoles.map(String)
      : ["chaser"],
    marchInterruptMul: Number(c?.marchInterruptMul) || 0.55,
    marchInterruptStandoffMul: Number(c?.marchInterruptStandoffMul) || 0.22,
    marchInterruptCapPx: Number(c?.marchInterruptCapPx) || 96,
    leashSlackPx: Number(c?.leashSlackPx) || 80,
    engageAcquirePadPx: Number(c?.engageAcquirePadPx) || 40,
    engageLeashAcquireMul: Number(c?.engageLeashAcquireMul) || 0.85,
    leashPullbackGoalBias: Math.max(
      0,
      Math.min(1, Number(c?.leashPullbackGoalBias) ?? 0.2),
    ),
  };
}

/** Master-leash pullback bias (0 = straight to master, 1 = stay on goal). */
export function leashPullbackGoalBias(): number {
  return combatParams().leashPullbackGoalBias;
}

export function threatRadius(e: SimEntity): number {
  return e.atkRange * combatParams().threatMul;
}

/**
 * Forced-move interrupt band — much tighter than combat threatRadius.
 * Ranged/standoff (chaser) must stay orderable without snapping to distant foes.
 */
export function marchInterruptRadius(e: SimEntity): number {
  const p = combatParams();
  const profile = hitProfileFor(e.role);
  const mul = profile.standoff ? p.marchInterruptStandoffMul : p.marchInterruptMul;
  const r = e.atkRange * mul;
  return Math.max(40, Math.min(r, p.marchInterruptCapPx));
}

/** Clear forced march only when an enemy is inside the short interrupt band. */
export function tryInterruptMarchOnThreat(
  e: SimEntity,
  enemies: SimEntity[],
): void {
  if (!e.marching) return;
  if (nearestInRadius(e, enemies, marchInterruptRadius(e))) {
    e.marching = false;
    e.combatSleepUntil = 0;
  }
}

export function attackStyleFor(role: UnitRole): string {
  const rig = C.rig as
    | {
        defaults?: { attackStyle?: string };
        byRole?: Record<string, { attackStyle?: string }>;
      }
    | undefined;
  return (
    rig?.byRole?.[role]?.attackStyle ??
    rig?.defaults?.attackStyle ??
    "slash"
  );
}

export function swingSecFor(role: UnitRole): number {
  const rig = C.rig as
    | {
        defaults?: { swingSec?: number };
        byRole?: Record<string, { swingSec?: number }>;
      }
    | undefined;
  return (
    Number(rig?.byRole?.[role]?.swingSec ?? rig?.defaults?.swingSec) || 0.28
  );
}

export function isRangedRole(role: UnitRole): boolean {
  return combatParams().projectileRoles.includes(role);
}

function nearestInRadius(
  self: SimEntity,
  enemies: SimEntity[],
  maxD: number,
): SimEntity | null {
  let best: SimEntity | null = null;
  let bestD = Infinity;
  for (let i = 0; i < enemies.length; i++) {
    const o = enemies[i]!;
    const d = dist(self.x, self.y, o.x, o.y);
    if (d > maxD) continue;
    if (d < bestD) {
      bestD = d;
      best = o;
    }
  }
  return best;
}

/**
 * RTS: nearest threat in threatR, else nearest in army leash/acquire band.
 */
export function pickCombatTarget(
  e: SimEntity,
  master: SimEntity,
  enemies: SimEntity[],
): SimEntity | null {
  if (enemies.length === 0) return null;

  const threat = nearestInRadius(e, enemies, threatRadius(e));
  if (threat) return threat;

  const leashR = e.leash;
  const nearMasterR = leashR + e.atkRange * combatParams().acquireMul;
  let best: SimEntity | null = null;
  let bestD = Infinity;
  for (let i = 0; i < enemies.length; i++) {
    const o = enemies[i]!;
    const dSelf = dist(e.x, e.y, o.x, o.y);
    const inBand =
      dSelf <= leashR || dist(o.x, o.y, master.x, master.y) <= nearMasterR;
    if (!inBand) continue;
    if (dSelf < bestD) {
      bestD = dSelf;
      best = o;
    }
  }
  return best;
}

/** Whether unit should enter combat steering vs goal follow / leash return. */
export function shouldEngage(
  e: SimEntity,
  master: SimEntity,
  tgt: SimEntity,
  _profile: HitProfile,
): boolean {
  const p = combatParams();
  const dMaster = dist(e.x, e.y, master.x, master.y);
  if (dMaster > e.leash + p.leashSlackPx) return false;
  const dT = dist(e.x, e.y, tgt.x, tgt.y);
  const threatR = threatRadius(e);
  if (dT <= threatR) return true;
  // Same site-chase band for melee and ranged — role feel comes from leash/atkRange/standoff.
  const acquireR = Math.max(
    e.atkRange + p.engageAcquirePadPx,
    e.leash * p.engageLeashAcquireMul,
  );
  return dT < acquireR;
}

function beginAttackAnim(e: SimEntity): void {
  e.animState = "attack";
  e.animTime = 0;
}

function atkIntervalFor(e: SimEntity, speedMul: number): number {
  let base: number;
  if (e.role === "master") base = C.master.atkInterval;
  else if (e.role === "hero") base = C.units.hero.atkInterval;
  else base = unitDef(e.role as TroopKind).atkInterval;
  return base / (speedMul > 1 ? 1.1 : 1);
}

function emitAttack(
  world: WorldState,
  e: SimEntity,
  tgt: SimEntity,
): void {
  emit(world, {
    kind: FfaEvent.Attack,
    time: world.time,
    attackerId: e.id,
    targetId: tgt.id,
    teamId: e.teamId,
    x0: e.x,
    y0: e.y,
    x1: tgt.x,
    y1: tgt.y,
    role: e.role,
    style: attackStyleFor(e.role),
    ranged: isRangedRole(e.role),
  });
}

/**
 * If in range and off cooldown: start attack anim, emit attack (presentation),
 * apply instant hits. Returns whether any kill occurred.
 */
export function tryFireAttack(
  world: WorldState,
  e: SimEntity,
  tgt: SimEntity,
  enemies: SimEntity[],
  speedMul = 1,
): boolean {
  const dT = dist(e.x, e.y, tgt.x, tgt.y);
  if (dT > e.atkRange || e.atkCd > 0) return false;
  e.atkCd = atkIntervalFor(e, speedMul);
  beginAttackAnim(e);
  emitAttack(world, e, tgt);
  return resolveAttackHits(world, e, tgt, enemies);
}

/**
 * Close / standoff / kite + optional fire. Returns whether any kill occurred.
 */
export function steerCombat(
  world: WorldState,
  e: SimEntity,
  tgt: SimEntity,
  enemies: SimEntity[],
  speedMul: number,
  profile: HitProfile,
): CombatSteerResult {
  applyFacingFromIntent(e, tgt.x - e.x, world.time);
  const dT = dist(e.x, e.y, tgt.x, tgt.y);
  const holdMul = profile.holdMul > 0 ? profile.holdMul : 0.85;
  const retreatMul = profile.standoff ? profile.retreatMul : 0;
  let killed = false;

  if (retreatMul > 0 && dT < e.atkRange * retreatMul) {
    const dx = e.x - tgt.x;
    const dy = e.y - tgt.y;
    const len = Math.hypot(dx, dy);
    const fx = len > 1e-4 ? e.x + (dx / len) * e.atkRange : e.x + e.atkRange;
    const fy = len > 1e-4 ? e.y + (dy / len) * e.atkRange : e.y;
    setDesiredToward(e, fx, fy, speedMul, world.time);
    if (tryFireAttack(world, e, tgt, enemies, speedMul)) killed = true;
  } else if (dT > e.atkRange * holdMul) {
    setDesiredToward(e, tgt.x, tgt.y, speedMul, world.time);
  } else {
    e.vx = 0;
    e.vy = 0;
    e.intentVx = 0;
    e.intentVy = 0;
    if (tryFireAttack(world, e, tgt, enemies, speedMul)) killed = true;
  }
  return { killed };
}

/** @deprecated alias for tests / call sites expecting old name */
export function pickTarget(
  e: SimEntity,
  master: SimEntity,
  enemies: SimEntity[],
  _world?: WorldState,
): SimEntity | null {
  return pickCombatTarget(e, master, enemies);
}
